// Authentication Guard Service
// This service ensures users are authenticated before accessing protected features

import { googleAuthService } from './legacy-google-auth.js';

export class AuthGuardService {
  constructor() {
    this.isCheckingAuth = false;
    // Remove the auth requirements for main menu - auth is now required for everything
    this.authRequiredPages = ['main-menu', 'voice-translation', 'text-translation', 'start-session', 'join-session', 'group-session'];
  }

  async checkAuthenticationRequired(pageId) {
    // All pages now require authentication
    return true;
  }

  async isAuthenticated() {
    try {
      // Initialize Google Auth if not already done
      await googleAuthService.initialize();

      // Check if user is signed in and validate session
      const isSignedIn = googleAuthService.isSignedIn();
      if (!isSignedIn) {
        return false;
      }

      // Validate the session (this will auto-refresh if needed)
      return await googleAuthService.validateSession();
    } catch (error) {
      console.error('Authentication check failed:', error);
      return false;
    }
  }

  async ensureAuthenticated() {
    if (this.isCheckingAuth) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!this.isCheckingAuth) {
            clearInterval(checkInterval);
            resolve(googleAuthService.isSignedIn());
          }
        }, 100);
      });
    }

    this.isCheckingAuth = true;

    try {
      // Initialize Google Auth if not already done
      await googleAuthService.initialize();

      // Check if user is already signed in and validate session
      const isSignedIn = googleAuthService.isSignedIn();
      if (isSignedIn) {
        // Validate the session (this will auto-refresh if needed)
        const isValid = await googleAuthService.validateSession();
        if (isValid) {
          this.isCheckingAuth = false;
          return true;
        }
      }

      // Try One Tap first — shows the native browser "Sign in as X" overlay
      const signedInViaTap = await googleAuthService.promptOneTap();
      if (signedInViaTap) {
        this.isCheckingAuth = false;
        return true;
      }

      // One Tap unavailable or dismissed — fall back to custom modal
      const result = await this.showAuthenticationUI();
      
      this.isCheckingAuth = false;
      return result;
    } catch (error) {
      console.error('Authentication check failed:', error);
      this.isCheckingAuth = false;
      return false; // Return false instead of throwing error for initial load
    }
  }

  async showAuthenticationUI() {
    return new Promise((resolve) => {
      const authModal = this.createAuthModal();
      document.body.appendChild(authModal);

      let resolved = false;
      const resolveOnce = (value) => {
        if (resolved) return;
        resolved = true;
        document.removeEventListener('keydown', handleEscape);
        if (document.body.contains(authModal)) document.body.removeChild(authModal);
        resolve(value);
      };

      // Listen for successful sign-in via the rendered button (uses same One Tap callback)
      let firstCall = true;
      googleAuthService.onAuthStateChanged((isSignedIn) => {
        if (firstCall) { firstCall = false; return; }
        if (isSignedIn) resolveOnce(true);
      });

      // Render the official Google Sign-In button into the modal container
      const btnContainer = authModal.querySelector('#google-signin-btn-container');
      googleAuthService.renderSignInButton(btnContainer);

      // Handle modal close
      authModal.querySelector('#auth-close-btn').addEventListener('click', () => resolveOnce(false));

      const handleEscape = (e) => {
        if (e.key === 'Escape') resolveOnce(false);
      };
      document.addEventListener('keydown', handleEscape);
    });
  }
  createAuthModal() {
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-sm w-full mx-4 p-8">
        <div class="flex justify-end mb-2">
          <button id="auth-close-btn" class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="text-center mb-6">
          <p class="text-gray-600 dark:text-gray-300 text-sm">
            Sign in with Google to continue.
          </p>
        </div>
        <div id="google-signin-btn-container" class="flex justify-center"></div>
      </div>
    `;
    return modal;
  }

  // Check if current page requires authentication
  async shouldRequireAuth(pageId) {
    return this.authRequiredPages.includes(pageId);
  }

  // Get current user info if authenticated
  getCurrentUser() {
    return googleAuthService.getUserInfo();
  }

  // Sign out the current user
  async signOut() {
    try {
      await googleAuthService.signOut();
      return true;
    } catch (error) {
      console.error('Sign out failed:', error);
      throw error;
    }
  }
}

// Singleton instance
export const authGuardService = new AuthGuardService();